import {
  ArrowsClockwise,
  Bank,
  CaretLeft,
  ShieldCheck,
  Swap,
  User,
  Warning,
} from "@phosphor-icons/react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { motion } from "framer-motion";
import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import * as yup from "yup";
import success_svg from "../../../assets/images/transaction-success.svg";
import CurrencyInput from "../../../components/CurrencyInput";
import { useAuth } from "../../../context/AuthContext";
import { useSendContext } from "../../../context/SendContext";
import { formatAsMoney } from "../../../helpers/numbers";
import Confirm from "../Confirm";
import useLivenessCheck from "../../../hooks/useLivenessCheck";
import useCreateLivenessSession from "../../../hooks/useCreateLivenessSession";
import { toast } from "sonner";

const schemaClamp = (max: number) => {
  return yup.object().shape({
    amount: yup.number(),
    // .min(50, "You can't send lower than ₦50")
    // .max(max, "Insufficient Balance"),
    note: yup.string().required("Naration is required"),
  });
};

const Amount = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState<string>();
  const [confirming, setConfirming] = useState(false);

  const [showHighValueConfirm, setShowHighValueConfirm] = useState(false);
  const [showLivenessModal, setShowLivenessModal] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<{
    amount: number;
    note: string;
    name: string;
    bank: string;
  } | null>(null);
  const pendingRef = useRef(pendingSubmission);
  pendingRef.current = pendingSubmission;
  const {
    setTransfer,
    transfer,
    makeTransfer,
    transfering,
    transferError,
    isTransferError,
    transferData,
  } = useSendContext();

  const { user, refetch } = useAuth();

  const isVisaroTransfer = useMemo(
    () => transfer?.transferType === "intra",
    [transfer]
  );

  const authorize = useCallback(() => {
    if (pin) {
      makeTransfer(pin);
    }
  }, [makeTransfer, pin]);

  const handleAuthorized = useCallback(() => {
    authorize();
  }, [authorize]);

  const proceed = (amount: number, note: string) => {
    setTransfer((prev) => ({
      ...prev,
      ...transfer,
      amount: amount.toString(),
      note,
    }));
    setConfirming(true);
  };

  const { createSession, isCreatingSession, sessionError } =
    useCreateLivenessSession();

  const [livenessSession, setLivenessSession] = useState<{
    sessionId: string;
    sessionToken: string;
  } | null>(null);

  const liveness = useLivenessCheck({
    sessionId: livenessSession?.sessionId ?? "",
    sessionToken: livenessSession?.sessionToken ?? "",
    onSuccess: () => {
      const pending = pendingRef.current;
      proceed(pending?.amount || 0, pending?.note || "");
      setShowLivenessModal(false);
    },
    onFailure: (err: any) => {
      console.error("Liveness check failed", err);
      toast.error("Liveness verification failed. Please try again.");
    },
  });

  const handleStartLiveness = async () => {
    try {
      const newSession = await createSession();
      setLivenessSession(newSession);
      setTimeout(() => liveness.start(), 0);
    } catch {
      // sessionError is set by the hook and displayed in the modal
    }
  };

  return (
    <>
      <div>
        {transfering && (
          <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
            <HashLoader />
          </div>
        )}
        <div className="relative w-full">
          {transferData && (
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{
                type: "tween",
                ease: "anticipate",
              }}
              className="flex items-center justify-center gap-10 px-5 py-20 text-center flex-col bg-white absolute top-0 left-0  w-full z-50 h-full"
            >
              <>
                <div className="font-semibold text-3xl">
                  Transaction Successful
                </div>
                <div className="flex items-center flex-col gap-8">
                  <img src={success_svg} alt="" />
                  <div className="text-2xl max-w-2xl text-center">
                    <div>
                      You have successfully sent{" "}
                      <span className="font-bold">
                        ₦ {transfer?.amount || 0}
                      </span>{" "}
                      to{" "}
                      <span className="font-bold">
                        {transfer?.recipientName}
                      </span>{" "}
                      {!isVisaroTransfer && (
                        <>
                          -{" "}
                          <span className="font-bold">
                            {transfer?.bank?.name}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-5 w-fit">
                  <Link
                    to="/home"
                    className="p-2 py-3 text-center bg-deepBlue text-white px-10 border-2 rounded-xl font-medium"
                  >
                    Return to Dashboard
                  </Link>

                  <Link
                    to={`/payments/history/tx/${transferData?.data?.id}`}
                    className="p-2 py-3 text-center bg-white text-deepBlue px-10 border-2 rounded-xl font-medium"
                  >
                    Share Reciept
                  </Link>
                </div>
              </>
            </motion.div>
          )}
          {confirming && (
            <Confirm
              onAuthorized={handleAuthorized}
              onPinChange={setPin}
              title="Confirm Transfer"
              setConfirming={setConfirming}
              description={
                <div>
                  You are about to send{" "}
                  <span className="font-bold">₦ {transfer?.amount || 0}</span>{" "}
                  to{" "}
                  <span className="font-bold">{transfer?.recipientName}</span>
                  {!isVisaroTransfer && (
                    <>
                      -{" "}
                      <span className="font-bold">{transfer?.bank?.name}</span>
                    </>
                  )}
                </div>
              }
            />
          )}
          <Formik
            validationSchema={schemaClamp(user?.wallet?.available_balance || 0)}
            validateOnChange
            initialValues={{
              amount: parseFloat(transfer?.amount || "0") || 0,
              note: "",
            }}
            onSubmit={({ amount, note }) => {
              if (amount >= 100000) {
                setPendingSubmission({
                  amount,
                  note,
                  name: transfer?.recipientName!!,
                  bank: isVisaroTransfer ? "Visaro" : transfer?.bank?.name!!,
                });
                setShowHighValueConfirm(true);
                return;
              }

              proceed(amount, note);
            }}
          >
            {({ isValid, values }) => {
              return (
                <Form className="flex items-center justify-center gap-10 px-5 py-20 text-center flex-col">
                  <div className="flex flex-col gap-2">
                    <div className="font-semibold text-3xl">
                      How much do you want to send?
                    </div>
                  </div>
                  <div className="col-span-full bg-white drop-shadow-sm p-5 rounded-xl flex flex-col gap-5 max-w-4xl w-full">
                    <div className="text-left flex items-center gap-2">
                      <span>
                        Available balance:{" "}
                        <span className="font-semibold">
                          {`₦ ${formatAsMoney(user?.wallet?.available_balance || 0)}`}
                        </span>
                      </span>
                      <span
                        onClick={() => {
                          refetch();
                        }}
                        className="cursor-pointer"
                      >
                        <ArrowsClockwise
                          className="text-visaro-primary-700"
                          weight="bold"
                        />
                      </span>
                    </div>
                    <div className="flex flex-col gap-5">
                      <div className="flex flex-col gap-2">
                        <Field
                          name="amount"
                          component={CurrencyInput}
                          id="amount"
                          type="number"
                          placeholder="Amount"
                        />
                        <ErrorMessage
                          name="amount"
                          component="div"
                          className="text-red-500 text-left"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Field
                          component="textarea"
                          name="note"
                          placeholder="Narration"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="note"
                          component="div"
                          className="text-red-500 text-left"
                        />
                      </div>
                      {isTransferError && (
                        <div className="text-red-500 text-left">
                          {transferError?.message}
                        </div>
                      )}
                      <div className="text-left">You're sending to:</div>
                      {transfer?.recipientName ? (
                        isVisaroTransfer ? (
                          <>
                            <div className="bg-gray-50 p-4 rounded-lg flex gap-4 items-center text-left">
                              <div className="bg-gray-100 rounded-full p-3 text-visaro-orange">
                                <User weight="bold" size={18} />
                              </div>
                              <div className="flex-1">
                                <div className="text-base md:text-lg font-medium">
                                  {transfer?.recipientName || "Unknown"}
                                </div>
                                <div className="text-sm text-gray-700">
                                  Visaro User
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="hover:bg-gray-100/50 cursor-pointer text-visaro-primary-700 rounded-full p-3"
                              >
                                <Swap weight="bold" size={18} />
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-gray-50 p-4 rounded-lg flex gap-4 items-center text-left">
                              <div className="text-visaro-orange rounded-full">
                                {transfer?.bank?.logo ? (
                                  <img
                                    src={transfer?.bank?.logo}
                                    alt={transfer?.bank?.name}
                                    className="w-8 h-8 object-cover rounded-full"
                                  />
                                ) : (
                                  <Bank weight="bold" size={18} />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="text-base md:text-lg font-medium">
                                  {transfer?.recipientName || "Unknown"} (
                                  {transfer?.accountNumber ||
                                    "No Account Number"}
                                  )
                                </div>
                                <div className="text-sm text-gray-700">
                                  <span className="capitalize">
                                    {transfer?.bank?.name || "Unknown Bank"}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="hover:bg-gray-100/50 cursor-pointer text-visaro-primary-700 rounded-full p-3"
                              >
                                <Swap weight="bold" size={18} />
                              </button>
                            </div>
                          </>
                        )
                      ) : (
                        <>
                          <div className="bg-gray-50 p-4 rounded-lg flex gap-4 items-center text-left">
                            <div className="bg-gray-100 rounded-full p-3">
                              <User weight="bold" size={18} />
                            </div>
                            <div className="flex-1">
                              <div className="text-lg font-medium">
                                Select Recipient
                              </div>
                              <div className="text-sm text-gray-700">
                                You've not selected any account
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate("/send")}
                              className="hover:bg-gray-100/50 cursor-pointer text-visaro-primary-700 rounded-lg p-3 px-4"
                            >
                              Select
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between w-full max-w-4xl">
                    <button
                      onClick={() => navigate(-1)}
                      type="button"
                      className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                    >
                      <CaretLeft />
                      <span>Back</span>
                    </button>
                    <button
                      disabled={!isValid}
                      type="submit"
                      className="p-2 py-3 text-center bg-deepBlue text-white px-10 border-2 rounded-xl font-medium"
                    >
                      Proceed to Send
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
      {showHighValueConfirm && pendingSubmission && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed top-0 left-0 w-full h-screen z-50 bg-gray-500/60 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="bg-white w-full max-w-md rounded-2xl shadow-lg p-6 flex flex-col items-center gap-5"
          >
            <div className="p-2 rounded-full bg-orange-100 text-orange-500">
              <Warning size={40} />
            </div>

            <h2 className="text-xl font-semibold text-center">
              High-Value Transfer
            </h2>

            <div className="bg-orange-50/70 px-4 py-2 rounded-lg flex items-center gap-2">
              <span className="text-gray-700 font-medium">Amount :</span>
              <span className="font-bold">
                ₦{formatAsMoney(pendingSubmission?.amount || 0)}
              </span>
            </div>

            <p className="text-center text-gray-700">
              You're about to send more than{" "}
              <span className="font-semibold">₦100,000</span>. Please confirm
              that this is intentional.
            </p>

            <div className="bg-orange-50/70 p-4 rounded-xl w-full space-y-3">
              <h3 className="font-semibold text-sm">Transaction Details</h3>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">Name :</span>
                <span className="font-semibold">{pendingSubmission?.name}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">Bank :</span>
                <span className="font-semibold text-right">
                  {pendingSubmission?.bank}
                </span>
              </div>
            </div>

            <div className="flex gap-4 w-full">
              <button
                type="button"
                onClick={() => {
                  setShowHighValueConfirm(false);
                  setPendingSubmission(null);
                }}
                className="flex-1 border-2 border-orange-500 text-orange-500 font-semibold p-2 py-3 rounded-lg bg-orange-50 "
              >
                Reconfirm
              </button>

              <button
                onClick={() => {
                  if (pendingSubmission) {
                    // If the amount is >= 500,000, show liveness modal instead of proceeding directly
                    if (pendingSubmission.amount >= 500000) {
                      setShowLivenessModal(true);
                    } else {
                      proceed(pendingSubmission.amount, pendingSubmission.note);
                    }
                  }
                  setShowHighValueConfirm(false);
                  setPendingSubmission(null);
                }}
                className="flex-1 bg-orange-500 text-white font-semibold p-2 py-3 rounded-lg"
              >
                Proceed
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      {showLivenessModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed top-0 left-0 w-full h-screen z-50 bg-gray-500/60 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="bg-white w-full max-w-md rounded-2xl shadow-lg p-6 flex flex-col items-center gap-5"
          >
            <div className="p-5 bg-gray-50 rounded-full">
              <ShieldCheck size={40} className="text-deepBlue" />
            </div>
            <h2 className="text-xl font-semibold text-center">
              Liveness Verification
            </h2>
            <p className="text-gray-700 text-center">
              For transactions above <b>₦500,000</b>, we require a quick
              liveness verification to ensure the security of your funds.
            </p>
            {(sessionError || liveness.error) && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-2 w-full text-center">
                {sessionError || liveness.error}
              </div>
            )}
            <button
              className="bg-deepBlue text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isCreatingSession || liveness.status === "running"}
              onClick={handleStartLiveness}
            >
              {isCreatingSession
                ? "Preparing..."
                : liveness.status === "running"
                ? "Starting..."
                : "Start Liveness Check"}
            </button>
            <button
              className="mt-3 border border-gray-400 px-6 py-3 rounded-lg"
              onClick={() => setShowLivenessModal(false)}
            >
              Cancel
            </button>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Amount;
